/**
 * Google Analytics 4 for the React SPA — hybrid delivery.
 *
 * Two channels, both gated on the shared `cookie_consent_v1` decision
 * (see consent.ts); nothing is sent before consent:
 *
 *   1. **GTM (dataLayer)** — `page_view` + user-property context. GA4 enhanced
 *      measurement and session attribution live here. Container id is
 *      `VITE_GTM_ID` (defaults to the marketing-site container).
 *
 *   2. **Server proxy** — custom product events (`sign_up`, `goal_created`, …)
 *      are POSTed to `/api/analytics/collect`, which forwards them to GA4 via
 *      the Measurement Protocol. This bypasses GTM entirely (the container's
 *      GA4 tag is currently malware-flagged by Google's scanner) while still
 *      stitching to the same GA4 user: we send the GA `_ga` client id so proxy
 *      events and GTM page_views share one identity. See docs/ANALYTICS.md.
 *
 * Account/user dimensions (org_id, role, plan, …) are set once on auth and
 * attached to every event. No PII (emails, names, free text) is ever sent.
 */

import { hasStatisticsConsent, onConsentAccepted } from './consent';
import { AnalyticsEvent, type AnalyticsEventName, type AnalyticsParams } from './analyticsEvents';

const GTM_CONTAINER_ID = (import.meta.env.VITE_GTM_ID as string | undefined) || 'GTM-P5GLWHNB';
// Mirror services/api/client.ts: same-origin in prod, VITE_API_URL in dev.
const API_BASE = import.meta.env.PROD ? '' : (import.meta.env.VITE_API_URL ?? '');
const COLLECT_URL = `${API_BASE}/api/analytics/collect`;

/** dataLayer entry — GTM reads this array; the `event` key triggers tags. */
interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    dataLayer?: DataLayerEvent[];
  }
}

export interface AnalyticsDimensions {
  org_id?: string;
  user_id?: string;
  org_plan?: string;
  user_role?: string;
  account_type?: 'demo' | 'trial' | 'customer';
  org_size_bucket?: string;
  is_demo?: boolean;
}

let dimensions: AnalyticsDimensions = {};
let gtmLoaded = false;
let consentGranted = false;

// ── Pre-consent buffers (flushed on opt-in; capped so they can't grow) ──
const dlPreConsent: DataLayerEvent[] = [];
const eventPreConsent: Array<{ name: string; params: AnalyticsParams }> = [];
const MAX_BUFFER = 50;

// ── Proxy queue (post-consent custom events awaiting a batched flush) ──
const pendingEvents: Array<{ name: string; params: AnalyticsParams }> = [];
const FLUSH_DEBOUNCE_MS = 2000;
const MAX_BATCH = 25;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// ─────────────────────────────────────────────────────────────────────
// GTM (page_view + user properties)
// ─────────────────────────────────────────────────────────────────────

function ensureDataLayer(): DataLayerEvent[] {
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function loadGtmScript(): void {
  if (gtmLoaded || typeof document === 'undefined' || !GTM_CONTAINER_ID) return;
  if (document.querySelector('script[data-gtm]')) {
    gtmLoaded = true;
    return;
  }
  gtmLoaded = true;
  const dl = ensureDataLayer();
  dl.push({ event: 'gtm.js', 'gtm.start': Date.now() } as DataLayerEvent);
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  script.dataset.gtm = 'true';
  document.head.appendChild(script);
}

function pushDataLayer(entry: DataLayerEvent): void {
  if (typeof window === 'undefined') return;
  if (consentGranted) {
    ensureDataLayer().push(entry);
  } else if (dlPreConsent.length < MAX_BUFFER) {
    dlPreConsent.push(entry);
  }
}

// ─────────────────────────────────────────────────────────────────────
// GA4 client id (so proxy events join the same user as GTM page_views)
// ─────────────────────────────────────────────────────────────────────

const CID_FALLBACK_KEY = 'harmny:ga_cid';

/** Read GA's `_ga` cookie client id (`<random>.<ts>`); fall back to a stable
 * locally-persisted id if GTM/GA hasn't written the cookie yet. */
function getClientId(): string {
  try {
    const m = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.([\d.]+)/);
    if (m && m[1]) return m[1];
  } catch {
    /* cookies blocked */
  }
  try {
    let id = localStorage.getItem(CID_FALLBACK_KEY);
    if (!id) {
      id = `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
      localStorage.setItem(CID_FALLBACK_KEY, id);
    }
    return id;
  } catch {
    return `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Proxy channel (custom events → /api/analytics/collect → GA4 MP)
// ─────────────────────────────────────────────────────────────────────

function buildPayload(batch: Array<{ name: string; params: AnalyticsParams }>): string {
  return JSON.stringify({
    client_id: getClientId(),
    user_id: dimensions.user_id,
    events: batch.map((e) => ({ name: e.name, params: e.params })),
  });
}

/** Send queued events. `useBeacon` for page-hide (survives unload). */
function flushEvents(useBeacon = false): void {
  if (typeof window === 'undefined' || !consentGranted || pendingEvents.length === 0) return;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = pendingEvents.splice(0, MAX_BATCH);
  const body = buildPayload(batch);
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: 'application/json' }));
    } else {
      void fetch(COLLECT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'include',
        keepalive: true,
      }).catch(() => {
        /* best-effort; a dropped analytics event is non-fatal */
      });
    }
  } catch {
    /* sendBeacon/fetch unavailable — drop silently */
  }
  // More than one batch queued → keep draining.
  if (pendingEvents.length > 0) scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents(false);
  }, FLUSH_DEBOUNCE_MS);
}

function enqueueEvent(name: string, params: AnalyticsParams): void {
  if (consentGranted) {
    pendingEvents.push({ name, params });
    scheduleFlush();
  } else if (eventPreConsent.length < MAX_BUFFER) {
    eventPreConsent.push({ name, params });
  }
}

// ─────────────────────────────────────────────────────────────────────
// Consent lifecycle
// ─────────────────────────────────────────────────────────────────────

function activateConsent(): void {
  consentGranted = true;
  loadGtmScript();
  // Flush buffered dataLayer entries (page_view, user props).
  if (dlPreConsent.length) {
    const dl = ensureDataLayer();
    for (const e of dlPreConsent) dl.push(e);
    dlPreConsent.length = 0;
  }
  // Move buffered custom events into the proxy queue and flush.
  if (eventPreConsent.length) {
    pendingEvents.push(...eventPreConsent);
    eventPreConsent.length = 0;
  }
  if (pendingEvents.length) flushEvents(false);
}

/** Bootstrap analytics. Idempotent; call once from main.tsx. */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (hasStatisticsConsent()) {
    activateConsent();
  } else {
    onConsentAccepted(activateConsent);
  }
  // Flush in-flight events when the tab is hidden/closed (survives unload).
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents(true);
  });
  window.addEventListener('pagehide', () => flushEvents(true));
}

// ─────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────

/** Set account/user dimensions attached to every event. Call on sign-in and
 * whenever the effective role changes. Also enriches GTM page_views. */
export function setAnalyticsUser(next: AnalyticsDimensions): void {
  dimensions = { ...next };
  pushDataLayer({ event: 'set_user_properties', ...dimensions });
}

/** Clear dimensions on sign-out so anonymous events aren't tagged to an org. */
export function clearAnalyticsUser(): void {
  dimensions = {};
}

/**
 * Fire a product-analytics custom event (catalog-typed). Merged with account
 * dimensions and sent via the server proxy. `page_view` does NOT come here —
 * use `trackPageView` (GTM).
 */
export function track(event: AnalyticsEventName, params: AnalyticsParams = {}): void {
  if (typeof window === 'undefined') return;
  const merged: AnalyticsParams = { ...dimensions };
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) merged[k] = v;
  }
  enqueueEvent(event, merged);
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Collapse dynamic URL segments to `:id` so GA4 page reports stay low
 * cardinality AND can't leak PII. A segment is dynamic if it's a UUID, contains
 * a digit (record ids / permalink slugs carry an id suffix), or is unusually
 * long. Static route words are preserved.
 */
export function maskPath(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      if (UUID_RE.test(seg) || /\d/.test(seg) || seg.length > 24) return ':id';
      return seg;
    })
    .join('/');
}

/**
 * Manual SPA `page_view` via GTM (GA4 enhanced measurement can't see client
 * routing inside the `/app` BrowserRouter). Mask the path with `maskPath`
 * first. Page views stay on GTM so GA4 session/scroll/engagement still works.
 */
export function trackPageView(path: string, title?: string): void {
  pushDataLayer({
    event: AnalyticsEvent.PageView,
    ...dimensions,
    page_path: path,
    page_title: title ?? (typeof document !== 'undefined' ? document.title : undefined),
  });
}
